import { Flex, Group, ScrollArea, SimpleGrid, Stack } from "@mantine/core";
import { useCallback, useEffect, useRef, useState } from "react";

import type { AgentActivity } from "@/sdk";

import { useAnalytics } from "@/analytics";
import { useAgentFriendlyName, useAgentSdk, useAgentStore, useLogger } from "@/sdk";

import { CustomerLoveCard } from "./cards/CustomerLoveCard";
import { QualityEfficiencyCard } from "./cards/QualityEfficiencyCard";
import { SalesOpportunitiesCard } from "./cards/SalesOpportunitiesCard";
import { useYesterdayPerformanceMetrics } from "./data/useYesterdayPerformanceMetrics";
import { AUTO_DISMISS_MS, AutoDismissCountdown } from "./greeting/AutoDismissCountdown";
import { Greeting } from "./greeting/Greeting";
import { MyMetricsHeader } from "./metrics/MyMetricsHeader";
import styles from "./morningPaper.module.css";
import { useMorningPaperStore } from "./store/morningPaper.store";
import { useMorningPaperShiftRitual } from "./store/useMorningPaperShiftRitual";

export function MorningPaper() {
    const firstName = useAgentFriendlyName();
    const dismissForToday = useMorningPaperStore((state) => state.dismissForToday);
    const { metrics, isLoading } = useYesterdayPerformanceMetrics();
    const { dispatcher } = useAnalytics();
    const { startShift } = useMorningPaperShiftRitual();
    const logger = useLogger({ module: "MorningPaper" });
    const sdk = useAgentSdk();
    const currentActivity = useAgentStore((state) => state.activity);
    const previousActivity = useRef<AgentActivity | undefined>(undefined);
    const hasDispatchedShown = useRef(false);
    const hasDispatchedMetricsLoaded = useRef(false);
    const [isDismissing, setIsDismissing] = useState(false);
    const isDismissingRef = useRef(false);

    useEffect(() => {
        if (hasDispatchedShown.current) return;
        hasDispatchedShown.current = true;
        void dispatcher.dispatchBusinessEvent("MorningPaperShown");
    }, [dispatcher]);

    useEffect(() => {
        if (isLoading || hasDispatchedMetricsLoaded.current) return;
        hasDispatchedMetricsLoaded.current = true;
        void dispatcher.dispatchBusinessEvent("MorningPaperMetricsLoaded", {
            hasRating: metrics?.fiveStarRating != null,
            hasTransferRate: metrics?.transferRate != null,
            hasSales: metrics?.netEnrolls != null,
        });
    }, [isLoading, metrics, dispatcher]);

    const handleDismiss = useCallback(async () => {
        if (isDismissingRef.current) return;
        isDismissingRef.current = true;
        setIsDismissing(true);
        try {
            void dispatcher.dispatchBusinessEvent("MorningPaperDismissed");
            await startShift();
            dismissForToday();
        } catch (err: unknown) {
            logger.error({ err }, "Morning paper: dismiss handler failed");
            isDismissingRef.current = false;
            setIsDismissing(false);
        }
    }, [dispatcher, startShift, dismissForToday, logger]);

    const handleAutoDismiss = useCallback(() => {
        logger.info(
            { autoDismissMs: AUTO_DISMISS_MS },
            "Morning paper: auto-dismissing after inactivity timeout — flipping agent to Available",
        );
        void handleDismiss();
    }, [handleDismiss, logger]);

    useEffect(() => {
        const previous = previousActivity.current;
        previousActivity.current = currentActivity;

        if (!previous) return; // First commit — no transition to detect
        if (previous === sdk.availableAgentActivity) return;
        if (currentActivity !== sdk.availableAgentActivity) return; // Not transitioning to Available

        logger.info(
            { from: previous, to: currentActivity },
            "Morning paper: agent flipped to Available externally — auto-dismissing",
        );
        void handleDismiss();
    }, [currentActivity, sdk.availableAgentActivity, handleDismiss, logger]);

    return (
        <Flex className={styles.root} role="region" aria-label="Morning paper">
            <ScrollArea classNames={{ root: styles.scrollRoot, viewport: styles.scrollViewport }} type="auto">
                <Stack className={styles.content} gap="xl">
                    <Group justify="flex-end" className={styles.topBar}>
                        <AutoDismissCountdown durationMs={AUTO_DISMISS_MS} onTimeout={handleAutoDismiss} />
                    </Group>

                    <Greeting name={firstName} onDismiss={handleDismiss} isDismissing={isDismissing} />

                    <Stack gap="md">
                        <MyMetricsHeader />
                        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                            <CustomerLoveCard
                                view={{
                                    yesterdayRating: metrics?.fiveStarRating ?? null,
                                    isLoading,
                                }}
                            />
                            <QualityEfficiencyCard
                                view={{
                                    transferRatePercent: metrics?.transferRate ?? null,
                                    isLoading,
                                }}
                            />
                            <SalesOpportunitiesCard
                                view={{
                                    sales: metrics?.netEnrolls ?? null,
                                    isLoading,
                                }}
                            />
                        </SimpleGrid>
                    </Stack>
                </Stack>
            </ScrollArea>
        </Flex>
    );
}
